// 5連窓スキャナ
//
// 盤面の「5マス連続の窓」(4方向 × 起点) を基準に、連続・ギャップ問わず
// 「5連まであと何手か」をカウントする。従来の analyzeDirection は
// 連続石しか検出できず、飛び4 (例: B B B _ B) のような 1手で5連に届く
// パターンを見逃していたため、脅威評価を窓単位に置き換える。
//
// 定義:
//   - 5連窓 = 4方向どれかで5マス連続した領域
//   - 相手石を1つでも含む窓はスキップ（その窓では5連不可能）
//   - 自石数 s のみで分類: s=5 は勝利、s=4 は「1手で5連」、s=3 は「2手で5連」…
//
// 性質:
//   - 連続 open-4 (_BBBB_) は s=4 窓が 2 つ → 自動的に2倍評価
//   - 片開き4連は s=4 窓が 1 つ
//   - 飛び4 (BBB_B など) も s=4 窓が 1 つ → 同じ脅威レベルで検出される
//   - open-3 (_BBB_) は s=3 窓が 3 つ、片開き 3 連は 2 つ、などパターンごとに自然な差がつく

import { BOARD_SIZE, WIN_LENGTH } from '../config/board.ts'
import { DIRECTIONS } from './engine.ts'
import type { Cell } from './types.ts'

export interface ThreatCounts {
  // 5石揃い窓（勝利状態）。checkWin と重複するため通常使わない
  five: number
  // 4石+1空き 窓: 1手で5連。closed-4 / 飛び4 / open-4 の構成要素
  four: number
  // 3石+2空き 窓: 2手で5連。open-3 / gap-3 など
  three: number
  // 2石+3空き 窓: 足場
  two: number
}

export function emptyThreatCounts(): ThreatCounts {
  return { five: 0, four: 0, three: 0, two: 0 }
}

function classifyWindow(counts: ThreatCounts, stones: number): void {
  if (stones === 5) counts.five++
  else if (stones === 4) counts.four++
  else if (stones === 3) counts.three++
  else if (stones === 2) counts.two++
}

// 盤面全体を走査して色ごとの脅威窓数を返す（minimax staticEval 用）
export function scanFullBoard(board: Cell[][], color: 'black' | 'white'): ThreatCounts {
  const counts = emptyThreatCounts()
  const opp: Cell = color === 'black' ? 'white' : 'black'

  for (const [dr, dc] of DIRECTIONS) {
    for (let r = 0; r < BOARD_SIZE; r++) {
      for (let c = 0; c < BOARD_SIZE; c++) {
        const er = r + dr * (WIN_LENGTH - 1)
        const ec = c + dc * (WIN_LENGTH - 1)
        if (er < 0 || er >= BOARD_SIZE || ec < 0 || ec >= BOARD_SIZE) continue

        let stones = 0
        let blocked = false
        for (let k = 0; k < WIN_LENGTH; k++) {
          const cell = board[r + dr * k][c + dc * k]
          if (cell === color) stones++
          else if (cell === opp) {
            blocked = true
            break
          }
        }
        if (blocked) continue
        classifyWindow(counts, stones)
      }
    }
  }
  return counts
}

// 指定マス (row, col) を通る 5連窓のみ走査（greedy の evalPosition 用）
// placeStone 後の盤面を前提とし、window 内の対象マスの状態が更新されているか
// は呼び出し側の責任。
export function scanThroughPoint(
  board: Cell[][],
  row: number,
  col: number,
  color: 'black' | 'white'
): ThreatCounts {
  const counts = emptyThreatCounts()
  const opp: Cell = color === 'black' ? 'white' : 'black'

  for (const [dr, dc] of DIRECTIONS) {
    // (row, col) を含む窓は、窓の開始点が (row - k*dr, col - k*dc) で k=0..WIN_LENGTH-1
    for (let k = 0; k < WIN_LENGTH; k++) {
      const sr = row - dr * k
      const sc = col - dc * k
      const er = sr + dr * (WIN_LENGTH - 1)
      const ec = sc + dc * (WIN_LENGTH - 1)
      if (sr < 0 || sr >= BOARD_SIZE || sc < 0 || sc >= BOARD_SIZE) continue
      if (er < 0 || er >= BOARD_SIZE || ec < 0 || ec >= BOARD_SIZE) continue

      let stones = 0
      let blocked = false
      for (let i = 0; i < WIN_LENGTH; i++) {
        const cell = board[sr + dr * i][sc + dc * i]
        if (cell === color) stones++
        else if (cell === opp) {
          blocked = true
          break
        }
      }
      if (blocked) continue
      classifyWindow(counts, stones)
    }
  }
  return counts
}
